import { useEffect, useRef, useState } from 'react';
import { syncCurrentUserCookieToFirebase } from '../utils/videoUtils';

interface UseVideoTrackerOptions {
  videoId: string;
  userId: string;
  completionThreshold?: number; // Default 95%
}

interface CookieProgress {
  watchTime: number;
  lastPosition: number;
  completed: boolean;
  lastSync: number;
  dirty: boolean;
  viewCount?: number;
  duration?: number;
  hasStarted?: boolean;
}

export function useVideoTracker({ 
  videoId, 
  userId, 
  completionThreshold = 0.95 
}: UseVideoTrackerOptions) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<{
    lastPosition: number;
    duration: number;
    completed: boolean;
    watchTime: number;
    viewCount: number;
    hasStarted: boolean;
  }>({
    lastPosition: 0,
    duration: 0,
    completed: false,
    watchTime: 0,
    viewCount: 0,
    hasStarted: false
  });

  // Cookie management
  const cookieName = `videoProgress_${videoId}`;
  const lastWatchTimeUpdateRef = useRef(0);
  const isPlayingRef = useRef(false);
  const lastSyncRef = useRef(0);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Sync function with rate limiting
  const syncToFirebase = async () => {
    const now = Date.now();
    const timeSinceLastSync = now - lastSyncRef.current;
    
    // Only sync every 30 seconds to avoid overloading Firebase
    if (timeSinceLastSync < 30000) {
      return;
    }
    
    if (userId) {
      try {
        await syncCurrentUserCookieToFirebase(userId);
        lastSyncRef.current = now;
      } catch (error) {
        console.error('Error syncing to Firebase:', error);
      }
    }
  };

  // Cookie utilities
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  const setCookie = (name: string, value: string, days: number = 30) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  // Load progress from cookie only (no Firebase)
  const loadProgress = async (): Promise<CookieProgress> => {
    const cookieData = getCookie(cookieName);
    
    if (cookieData) {
      try {
        const parsed = JSON.parse(cookieData) as CookieProgress;
        return parsed;
      } catch (error) {
        console.error('Error parsing cookie data:', error);
      }
    }

    // Create new cookie data if none exists
    const newData: CookieProgress = {
      watchTime: 0,
      lastPosition: 0,
      completed: false,
      lastSync: Date.now(),
      dirty: false,
      viewCount: 0,
      hasStarted: false
    };
    
    // Save to cookie
    setCookie(cookieName, JSON.stringify(newData));
    return newData;
  };

  // Save progress to cookie
  const saveProgressToCookie = (data: CookieProgress) => {
    setCookie(cookieName, JSON.stringify(data));
  };

  // Load saved progress on mount
  useEffect(() => {
    const fetchProgress = async () => {
      console.log('useVideoTracker: fetchProgress called', { userId, videoId });
      
      if (!userId || !videoId) {
        console.log('useVideoTracker: No userId or videoId, setting loading false');
        setLoading(false);
        setReady(true);
        return;
      }
      
      console.log('useVideoTracker: Setting loading true');
      setLoading(true);
      setReady(false);
      
      try {
        const cookieData = await loadProgress();
        
        const newProgress = {
          lastPosition: cookieData.lastPosition,
          duration: 0, // Will be set when video loads
          completed: cookieData.completed,
          watchTime: cookieData.watchTime,
          viewCount: cookieData.viewCount || 0,
          hasStarted: cookieData.hasStarted || false
        };
        
        setProgress(newProgress);
        
      } catch (error) {
        console.error('Error loading video progress:', error);
      } finally {
        console.log('useVideoTracker: Setting loading false and ready true');
        setLoading(false);
        setReady(true);
      }
    };

    fetchProgress();
  }, [userId, videoId]);

  // Set up video tracking
  useEffect(() => {
    console.log('useVideoTracker: Setting up video tracking', { ready, hasVideoRef: !!videoRef.current, userId, videoId });
    if (!ready || !videoRef.current || !userId || !videoId) {
      console.log('useVideoTracker: Not ready for video tracking, returning');
      return;
    }

    const video = videoRef.current;

    // Reset tracking state when video changes
    isPlayingRef.current = false;
    lastWatchTimeUpdateRef.current = 0;

    const onLoadedMetadata = () => {
      if (video.duration && video.duration > 0) {
        setProgress(prev => ({ ...prev, duration: video.duration }));
        
        // Save duration to cookie so it can be synced to Firebase
        const cookieData = getCookie(cookieName);
        if (cookieData) {
          try {
            const parsed = JSON.parse(cookieData) as CookieProgress;
            const updatedData: CookieProgress = {
              ...parsed,
              duration: video.duration,
              dirty: true
            };
            saveProgressToCookie(updatedData);
          } catch (error) {
            console.error('Error updating cookie with duration:', error);
          }
        }
        
        // Seek to last position if not completed
        if (cookieData) {
          try {
            const parsed = JSON.parse(cookieData) as CookieProgress;
            if (!parsed.completed && parsed.lastPosition > 0) {
              video.currentTime = parsed.lastPosition;
            }
          } catch (error) {
            console.error('Error parsing cookie for seek:', error);
          }
        }
        
        // If video is already playing when metadata loads, initialize timer
        if (!video.paused && isPlayingRef.current) {
          lastWatchTimeUpdateRef.current = performance.now();
        }
      }
    };

    const onPlay = () => {
      isPlayingRef.current = true;
      // Reset the last watch time update when video starts playing
      lastWatchTimeUpdateRef.current = performance.now();
    };

    const onPause = () => {
      isPlayingRef.current = false;
      
      // Sync to Firebase when user pauses
      syncToFirebase();
    };

    const onTimeUpdate = async () => {
      if (!video.duration || video.duration <= 0) return;
      
      // Simplified visibility check - only check if video has dimensions
      const rect = video.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return;
      }
      
      const currentTime = video.currentTime;
      const duration = video.duration;
      const percent = currentTime / duration;
      const now = performance.now();

      // Always update local progress state with current position
      setProgress(prev => {
        const newProgress = { ...prev, lastPosition: currentTime, duration: duration };
        return newProgress;
      });

      // Always save lastPosition to cookie, regardless of playing state
      const cookieData = getCookie(cookieName);
      if (cookieData) {
        try {
          const parsed = JSON.parse(cookieData) as CookieProgress;
          
          // Track video started when percent goes from 0 to >0 (ONLY ONCE)
          const shouldMarkStarted = percent > 0 && (!parsed.hasStarted || parsed.viewCount === 0);
          
          // Check if video just reached completion threshold
              const justCompleted = percent >= completionThreshold && !parsed.completed;
          
          const updatedData: CookieProgress = {
            ...parsed,
            lastPosition: currentTime,
            duration: duration,
            completed: percent >= completionThreshold,
            hasStarted: shouldMarkStarted ? true : parsed.hasStarted,
            viewCount: shouldMarkStarted ? (parsed.viewCount || 0) + 1 : (parsed.viewCount || 0),
            dirty: true
          };
          
              saveProgressToCookie(updatedData);

              // Reflect completion in local state immediately
              if (justCompleted) {
                setProgress(prev => ({ ...prev, completed: true }));
              }
          
          // Update state for viewCount if needed
          if (shouldMarkStarted) {
            setProgress(prev => ({ 
              ...prev, 
              viewCount: prev.viewCount + 1, 
              hasStarted: true 
            }));
          }
          
          // Sync immediately if video just completed
          if (justCompleted) {
            syncToFirebase();
          }
        } catch (error) {
          console.error('Error updating cookie:', error);
        }
      }

      // WATCHTIME TRACKING - Only track when actually playing
      const isActuallyPlaying = !video.paused && !video.ended && currentTime > 0;
      
      if (isActuallyPlaying) {
        // Update playing state if it wasn't set
        if (!isPlayingRef.current) {
          isPlayingRef.current = true;
          lastWatchTimeUpdateRef.current = now;
        }
        
        // If this is the first timeupdate after play started, initialize the timer
        if (lastWatchTimeUpdateRef.current === 0) {
          lastWatchTimeUpdateRef.current = now;
          return; // Skip this update, start tracking from next one
        }
        
        const timeSinceLastWatchUpdate = now - lastWatchTimeUpdateRef.current;
        
        // Track watch time every second (allowing up to 3 seconds for buffering)
        if (timeSinceLastWatchUpdate >= 500) {
          // Calculate watchtime increment based on actual time passed (in seconds)
          const watchTimeIncrement = Math.floor(timeSinceLastWatchUpdate / 1000);
          
          if (watchTimeIncrement > 0) {
            lastWatchTimeUpdateRef.current = now;
            
            // Update local state
            setProgress(prev => {
              const newProgress = {
                ...prev,
                watchTime: prev.watchTime + watchTimeIncrement
              };
              return newProgress;
            });
            
            // Update cookie with watch time
            const cookieData = getCookie(cookieName);
            if (cookieData) {
              try {
                const parsed = JSON.parse(cookieData) as CookieProgress;
                const updatedData: CookieProgress = {
                  ...parsed,
                  watchTime: parsed.watchTime + watchTimeIncrement,
                  dirty: true
                };
                saveProgressToCookie(updatedData);
                
                // Sync every 30 seconds while playing (handled by syncToFirebase rate limiting)
                syncToFirebase();
              } catch (error) {
                console.error('Error updating cookie with watch time:', error);
              }
            }
          }
        }
        
        // If gap is too long (>3 seconds), reset timer (probably buffering/seeking)
        if (timeSinceLastWatchUpdate > 3000) {
          lastWatchTimeUpdateRef.current = now;
        }
      } else {
        // Reset timer when video is not playing
        isPlayingRef.current = false;
        lastWatchTimeUpdateRef.current = 0;
      }
    };

      const onEnded = async () => {
      isPlayingRef.current = false;
      
      // Mark as completed in cookie only
      const cookieData = getCookie(cookieName);
      if (cookieData) {
        try {
          const parsed = JSON.parse(cookieData) as CookieProgress;
          const completedData: CookieProgress = {
            ...parsed,
            lastPosition: video.duration,
            completed: true,
            hasStarted: true,
            dirty: true
          };
          saveProgressToCookie(completedData);
            // Also update local state so listeners react immediately
            setProgress(prev => ({ ...prev, lastPosition: video.duration, duration: video.duration, completed: true }));
          
          // Sync to Firebase immediately when video completes
          syncToFirebase();
        } catch (error) {
          console.error('Error marking video as completed:', error);
        }
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);
    
    // Add page unload sync
    const handleBeforeUnload = () => {
      syncToFirebase();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      
      // Stop tracking when component unmounts
      isPlayingRef.current = false;
      lastWatchTimeUpdateRef.current = 0;
      
      // Sync one final time before unmounting
      syncToFirebase();
      
      // Clear sync interval if it exists
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [ready, userId, videoId, completionThreshold]);

  return {
    videoRef,
    loading,
    progress,
    ready
  };
} 